import * as React from 'react';
import { PlusIcon, TrashIcon } from 'lucide-react';
import { Button } from '@/shadcn/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shadcn/components/ui/dialog';
import { Input } from '@/shadcn/components/ui/input';
import { Label } from '@/shadcn/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shadcn/components/ui/select';
import { Switch } from '@/shadcn/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shadcn/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shadcn/components/ui/tabs';
import { TableEntityData, TableCellData } from '../StyleControls/types';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (tableData: TableEntityData) => void;
  initialData?: TableEntityData;
}

const MAX_COLUMNS = 8;

export const TableModal: React.FC<IProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const [tableData, setTableData] = React.useState<TableEntityData>(() =>
    initialData || {
      rows: [
        [
          { content: 'Header 1', isHeader: true },
          { content: 'Header 2', isHeader: true }
        ],
        [
          { content: 'Cell 1', isHeader: false },
          { content: 'Cell 2', isHeader: false }
        ]
      ],
      alignment: 'left',
      borderStyle: 'solid',
      headerRow: true,
      width: 'w-full'
    }
  );

  const [editingCell, setEditingCell] = React.useState<{ row: number; col: number } | null>(null);
  const [tempCellValue, setTempCellValue] = React.useState('');

  React.useEffect(() => {
    if (initialData) {
      setTableData(initialData);
    }
  }, [initialData, isOpen]);

  const addRow = (index: number) => {
    const newRow: TableCellData[] = Array(tableData.rows[0]?.length || 2).fill(null).map(() => ({
      content: '',
      isHeader: false,
    }));

    const newRows = [...tableData.rows];
    newRows.splice(index + 1, 0, newRow);

    setTableData({ ...tableData, rows: newRows });
  };

  const deleteRow = (index: number) => {
    if (tableData.rows.length <= 1) return;

    const newRows = tableData.rows.filter((_, i) => i !== index);
    setTableData({ ...tableData, rows: newRows });
  };

  const addColumn = (index: number) => {
    // Check if we've reached the maximum column limit
    if (tableData.rows[0]?.length >= MAX_COLUMNS) {
      return; // Don't add more columns if limit is reached
    }

    const newRows = tableData.rows.map((row) => {
      const newRow = [...row];
      newRow.splice(index + 1, 0, { content: '', isHeader: false });
      return newRow;
    });

    setTableData({ ...tableData, rows: newRows });
  };

  const deleteColumn = (index: number) => {
    if (tableData.rows[0]?.length <= 1) return;

    const newRows = tableData.rows.map((row) => row.filter((_, i) => i !== index));
    setTableData({ ...tableData, rows: newRows });
  };

  const updateCellContent = (rowIndex: number, colIndex: number, content: string) => {
    const newRows = tableData.rows.map((row, rIdx) =>
      (rIdx === rowIndex
        ? row.map((cell, cIdx) => (cIdx === colIndex ? { ...cell, content } : cell))
        : row)
    );

    setTableData({ ...tableData, rows: newRows });
  };

  const handleCellClick = (rowIndex: number, colIndex: number) => {
    setEditingCell({ row: rowIndex, col: colIndex });
    setTempCellValue(tableData.rows[rowIndex][colIndex].content);
  };

  const handleCellBlur = () => {
    if (editingCell) {
      updateCellContent(editingCell.row, editingCell.col, tempCellValue);
      setEditingCell(null);
      setTempCellValue('');
    }
  };

  const handleCellKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCellBlur();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setTempCellValue('');
    }
  };

  const handleSave = () => {
    onSave(tableData);
    onClose();
  };

  const handleCancel = () => {
    if (initialData) {
      setTableData(initialData);
    }
    setEditingCell(null);
    setTempCellValue('');
    onClose();
  };

  const getBorderStyle = () => {
    switch (tableData.borderStyle) {
      case 'solid': return 'border border-gray-300';
      case 'dashed': return 'border border-dashed border-gray-300';
      case 'none': return 'border-0';
      default: return 'border border-gray-200';
    }
  };

  const getTableAlignment = () => {
    switch (tableData.alignment) {
      case 'center': return 'mx-auto';
      case 'right': return 'ml-auto';
      default: return 'mr-auto';
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
          if (!open) onClose();
        }}
    >
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto !z-[99999] p-4">
        <DialogHeader>
          <DialogTitle>Table Editor</DialogTitle>
          <DialogDescription>
            Create and customize your table. Click on cells to edit content.
            {tableData.rows[0]?.length >= MAX_COLUMNS && (
              <span className="text-orange-600 font-medium block mt-1">
                Maximum column limit ({MAX_COLUMNS}) reached
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="editor" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="editor">Table Editor</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="editor" className="space-y-4">
            {/* Column count indicator */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                Columns: {tableData.rows[0]?.length || 0} / {MAX_COLUMNS}
              </span>
              {tableData.rows[0]?.length >= MAX_COLUMNS && (
                <span className="text-sm text-orange-600 font-medium">
                  Maximum columns reached
                </span>
              )}
            </div>

            {/* Table Editor */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className={`w-full ${getTableAlignment()}`}>
                <Table className={`${getBorderStyle()} ${tableData.width || 'w-full'} bg-white`}>
                  {tableData.headerRow && (
                    <TableHeader>
                      <TableRow>
                        {tableData.rows[0]?.map((cell, colIndex) => (
                          <TableHead key={colIndex} className="relative group/cell">
                            {editingCell?.row === 0 && editingCell?.col === colIndex ? (
                              <Input
                                value={tempCellValue}
                                onChange={(e) => setTempCellValue(e.target.value)}
                                onBlur={handleCellBlur}
                                onKeyDown={handleCellKeyDown}
                                className="h-8 text-sm font-medium"
                                autoFocus
                              />
                            ) : (
                              <div
                                className="cursor-text min-h-8 p-2 -m-2 rounded hover:bg-gray-100"
                                onClick={() => handleCellClick(0, colIndex)}
                              >
                                {cell.content || `Header ${colIndex + 1}`}
                              </div>
                            )}

                            {/* Column Controls */}
                            <div className="absolute -top-2 -right-2 opacity-0 group-hover/cell:opacity-100 transition-opacity flex gap-1">
                              <Button
                                aria-label="Add column"
                                title={tableData.rows[0]?.length >= MAX_COLUMNS ? `Maximum ${MAX_COLUMNS} columns allowed` : 'Add column'}
                                variant="outline"
                                size="sm"
                                onClick={() => addColumn(colIndex)}
                                className="h-6 w-6 p-0"
                                disabled={tableData.rows[0]?.length >= MAX_COLUMNS}
                              >
                                <PlusIcon className="h-3 w-3" />
                              </Button>
                              {tableData.rows[0].length > 1 && (
                                <Button
                                  variant="outline"
                                  aria-label="Delete column"
                                  title="Delete column"
                                  size="sm"
                                  onClick={() => deleteColumn(colIndex)}
                                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                >
                                  <TrashIcon className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                  )}

                  <TableBody>
                    {tableData.rows.slice(tableData.headerRow ? 1 : 0).map((row, rowIndex) => {
                      const actualRowIndex = tableData.headerRow ? rowIndex + 1 : rowIndex;
                      return (
                        <TableRow key={actualRowIndex} className="group/row">
                          {row.map((cell, colIndex) => (
                            <TableCell key={colIndex} className="relative group/cell">
                              {editingCell?.row === actualRowIndex && editingCell?.col === colIndex ? (
                                <Input
                                  value={tempCellValue}
                                  onChange={(e) => setTempCellValue(e.target.value)}
                                  onBlur={handleCellBlur}
                                  onKeyDown={handleCellKeyDown}
                                  className="h-8 text-sm"
                                  autoFocus
                                />
                              ) : (
                                <div
                                  className="cursor-text min-h-8 p-2 -m-2 rounded hover:bg-gray-100"
                                  onClick={() => handleCellClick(actualRowIndex, colIndex)}
                                >
                                  {cell.content || `Cell ${actualRowIndex + 1}-${colIndex + 1}`}
                                </div>
                              )}

                              {/* Column Controls for Body Cells */}
                              {!tableData.headerRow && (
                                <div className="absolute -top-2 -right-2 opacity-0 group-hover/cell:opacity-100 transition-opacity flex gap-1">
                                  <Button
                                    variant="outline"
                                    aria-label="Add column"
                                    title={tableData.rows[0]?.length >= MAX_COLUMNS ? `Maximum ${MAX_COLUMNS} columns allowed` : 'Add column'}
                                    size="sm"
                                    onClick={() => addColumn(colIndex)}
                                    className="h-6 w-6 p-0"
                                    disabled={tableData.rows[0]?.length >= MAX_COLUMNS}
                                  >
                                    <PlusIcon className="h-3 w-3" />
                                  </Button>
                                  {row.length > 1 && (
                                    <Button
                                      variant="outline"
                                      aria-label="Delete column"
                                      title="Delete column"
                                      size="sm"
                                      onClick={() => deleteColumn(colIndex)}
                                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                    >
                                      <TrashIcon className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              )}
                            </TableCell>
                          ))}

                          {/* Row Controls */}
                          <div className="absolute -right-10 top-1/2 -translate-y-1/2 opacity-0 group-hover/row:opacity-100 transition-opacity flex flex-col gap-1">
                            <Button
                              variant="outline"
                              aria-label="Add row"
                              title="Add row"
                              size="sm"
                              onClick={() => addRow(actualRowIndex)}
                              className="h-6 w-6 p-0"
                            >
                              <PlusIcon className="h-3 w-3" />
                            </Button>
                            {tableData.rows.length > 1 && (
                              <Button
                                variant="outline"
                                aria-label="Delete row"
                                title="Delete row"
                                size="sm"
                                onClick={() => deleteRow(actualRowIndex)}
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                              >
                                <TrashIcon className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {tableData.headerRow && tableData.rows.length === 1 && (
                <div className="mt-2 flex justify-center">
                  <Button variant="outline" size="sm" onClick={() => addRow(0)} aria-label="Add first row" title="Add first row">
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add first row
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            {/* Settings Preview */}
            <div className="border rounded-lg p-4 bg-gray-50 mb-4">
              <h4 className="text-sm font-medium mb-2 text-gray-700">Preview</h4>
              <div className={`w-full ${getTableAlignment()}`}>
                <Table className={`${getBorderStyle()} ${tableData.width || 'w-full'} bg-white`}>
                  {tableData.headerRow && (
                    <TableHeader>
                      <TableRow>
                        {tableData.rows[0]?.map((cell, colIndex) => (
                          <TableHead key={colIndex}>
                            {cell.content || `Header ${colIndex + 1}`}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                  )}

                  <TableBody>
                    {tableData.rows.slice(tableData.headerRow ? 1 : 0).slice(0, 2).map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {row.map((cell, colIndex) => (
                          <TableCell key={colIndex}>
                            {cell.content || `Cell ${rowIndex + 1}-${colIndex + 1}`}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="grid gap-4">
              {/* Table Alignment */}
              <div className="grid gap-2">
                <Label htmlFor="alignment">Table Alignment</Label>
                <Select
                  value={tableData.alignment}
                  onValueChange={(value: 'left' | 'center' | 'right') =>
                    setTableData({ ...tableData, alignment: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select alignment" />
                  </SelectTrigger>
                  <SelectContent className="z-[100000]">
                    <SelectItem value="left">Left</SelectItem>
                    <SelectItem value="center">Center</SelectItem>
                    <SelectItem value="right">Right</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Border Style */}
              <div className="grid gap-2">
                <Label htmlFor="borderStyle">Border Style</Label>
                <Select
                  value={tableData.borderStyle}
                  onValueChange={(value: 'none' | 'solid' | 'dashed') =>
                    setTableData({ ...tableData, borderStyle: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select border style" />
                  </SelectTrigger>
                  <SelectContent className="z-[100000]">
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="solid">Solid</SelectItem>
                    <SelectItem value="dashed">Dashed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Table Width */}
              <div className="grid gap-2">
                <Label htmlFor="width">Table Width</Label>
                <Select
                  value={tableData.width || 'w-full'}
                  onValueChange={(value: string) =>
                    setTableData({ ...tableData, width: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select width" />
                  </SelectTrigger>
                  <SelectContent className="z-[100000]">
                    <SelectItem value="w-full">Full Width</SelectItem>
                    <SelectItem value="w-3/4">75% Width</SelectItem>
                    <SelectItem value="w-1/2">50% Width</SelectItem>
                    <SelectItem value="w-1/3">33% Width</SelectItem>
                    <SelectItem value="w-1/4">25% Width</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Header Row Toggle */}
              <div className="flex items-center space-x-2">
                <Switch
                  id="headerRow"
                  checked={tableData.headerRow}
                  onCheckedChange={(checked) =>
                    setTableData({ ...tableData, headerRow: checked })}
                />
                <Label htmlFor="headerRow">Enable header row</Label>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            {initialData ? 'Update Table' : 'Insert Table'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

TableModal.displayName = 'TableModal';
